import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { ComplianceOverview, SupplierAssessment } from '../types/types';

export const useSupplierAssessments = (supplierOrgId?: string) => {
  const [assessments, setAssessments] = useState<SupplierAssessment[]>([]);
  const [complianceOverview, setComplianceOverview] =
    useState<ComplianceOverview | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchAssessments = async () => {
    if (!supplierOrgId) {
      setAssessments([]);
      setComplianceOverview(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // -------------------------
    // Fetch assessments
    // -------------------------
    const { data, error } = await supabase.rpc(
      'get_supplier_assessments_by_org_id',
      { supplier_org: supplierOrgId }
    );

    if (error) {
      console.error('Error fetching supplier assessments:', error);
      setError(error.message);
      setAssessments([]);
      setComplianceOverview(null);
      setIsLoading(false);
      return;
    }

    setAssessments(data ?? []);
    setIsLoading(false);

    const assessmentIds = (data ?? []).map(
      (a: SupplierAssessment) => a.assessment_id
    );

    if (assessmentIds.length === 0) {
      setComplianceOverview(null);
      return;
    }

    // -------------------------
    // Fetch compliance overview
    // -------------------------
    setOverviewLoading(true);

    const { data: overviewData, error: overviewError } =
      await supabase.functions.invoke('get-supplier-compliance', {
        body: { assessment_ids: assessmentIds },
      });

    if (overviewError) {
      console.error(
        'Error fetching supplier compliance overview:',
        overviewError
      );
      setError(overviewError.message);
      setComplianceOverview(null);
    } else {
      setComplianceOverview(overviewData?.overview ?? null);
    }

    setOverviewLoading(false);
  };

  useEffect(() => {
    fetchAssessments();
  }, [supplierOrgId]);

  return {
    assessments,
    complianceOverview,
    isLoading,
    overviewLoading,
    error,
    refetch: fetchAssessments,
  };
};
